import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createGrowthResource, fetchGrowthDashboard, fetchGrowthResource } from '../../services/growthService';
import AdvancedConfiguration from '../super-admin/components/AdvancedConfiguration';
import AdminRecordTable from '../super-admin/components/AdminRecordTable';
import PageHeader from '../super-admin/components/PageHeader';
import WorkspaceNav from '../super-admin/components/WorkspaceNav';
import { safeAdminError } from '../super-admin/components/adminPresentation';

export const GROWTH_SECTIONS = [
  ['dashboard', 'Growth Dashboard'], ['programs', 'Founding Organizer'], ['rules', 'Rules'], ['referralCodes', 'Referral Codes'], ['referrals', 'Referrals'],
  ['rewardDefinitions', 'Rewards & Credits'], ['rewardWallets', 'Reward Wallets'], ['rewardLedger', 'Reward Ledger'], ['achievementLevels', 'Achievement Levels'],
  ['achievementProgress', 'Achievement Progress'], ['campaigns', 'Campaign Engine'], ['campaignExecutions', 'Campaign History'], ['crmProfiles', 'CRM'],
  ['crmActivities', 'CRM Timeline'], ['reputation', 'Organizer Reputation'], ['trust', 'Tournament Trust'], ['events', 'Growth Events'],
];
const GROWTH_GROUPS = [
  { label: 'Review performance', items: GROWTH_SECTIONS.filter(([key]) => ['dashboard', 'events'].includes(key)) },
  { label: 'Acquire', items: GROWTH_SECTIONS.filter(([key]) => ['programs', 'referralCodes', 'referrals'].includes(key)) },
  { label: 'Activate', items: GROWTH_SECTIONS.filter(([key]) => ['campaigns', 'campaignExecutions'].includes(key)) },
  { label: 'Reward', items: GROWTH_SECTIONS.filter(([key]) => ['rewardDefinitions', 'rewardWallets', 'rewardLedger', 'achievementLevels', 'achievementProgress'].includes(key)) },
  { label: 'Retain', items: GROWTH_SECTIONS.filter(([key]) => ['crmProfiles', 'crmActivities', 'reputation', 'trust'].includes(key)) },
  { label: 'Configure', items: GROWTH_SECTIONS.filter(([key]) => key === 'rules') },
];

const READ_ONLY = new Set(['events', 'rewardLedger', 'achievementProgress', 'campaignExecutions', 'crmActivities', 'reputation', 'trust']);

const GrowthAdminPage = () => {
  const { token } = useAuth();
  const [section, setSection] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('{}');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const title = useMemo(() => GROWTH_SECTIONS.find(([key]) => key === section)?.[1], [section]);
  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const payload = section === 'dashboard' ? await fetchGrowthDashboard(token) : await fetchGrowthResource(section, token);
      setItems(section === 'dashboard' ? [payload] : payload.items || []);
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load growth records.'));
    } finally {
      setBusy(false);
    }
  }, [section, token]);
  useEffect(() => { if (token) load(); }, [load, token]);
  const create = async () => {
    if (!reason.trim()) return setError('A change reason is required.');
    setBusy(true);
    setError('');
    try {
      await createGrowthResource(section, JSON.parse(draft), token, reason.trim());
      setDraft('{}');
      setReason('');
      await load();
    } catch (err) {
      setError(safeAdminError(err, 'Unable to save this growth record.'));
      setBusy(false);
    }
  };
  return (
    <section className="sa-workspace">
      <PageHeader title="Growth" description="Operate organizer programs, referrals, rewards, campaigns, CRM, reputation, and tournament trust." />
      <WorkspaceNav groups={GROWTH_GROUPS} value={section} onChange={setSection} ariaLabel="Growth sections" />
      {error ? <div className="sa-error-state" role="alert"><strong>Growth data could not be loaded.</strong><span>{error}</span></div> : null}
      <section className="sa-panel" role="tabpanel">
        <header className="sa-panel__header"><div><h2>{title}</h2><p>Canonical growth configuration and operational records.</p></div><button className="sa-secondary-button" type="button" onClick={load} disabled={busy}>Refresh</button></header>
        {busy ? <div className="sa-table-skeleton" aria-label="Loading growth records" /> : <AdminRecordTable items={items} />}
      </section>
      {section !== 'dashboard' && !READ_ONLY.has(section) ? <AdvancedConfiguration title="Advanced growth configuration" description="Eligibility, thresholds, expiry, weights, and limits must come from approved database configuration." draft={draft} setDraft={setDraft} reason={reason} setReason={setReason} busy={busy} onCreate={create} textareaLabel="Growth record JSON" /> : null}
    </section>
  );
};

export default GrowthAdminPage;
